import React from "react";

import {
  FaArrowDown,
  FaArrowUp,
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaEye,
  FaEyeSlash,
  FaPlus,
  FaTrashAlt,
} from "react-icons/fa";

import TutorialStepCard from "./TutorialStepCard";

function sortByOrder(items = []) {
  return [...items].sort(
    (a, b) =>
      Number(a.ordre || 0) -
      Number(b.ordre || 0)
  );
}

export default function TutorialSectionPanel({
  section,
  index,
  total,
  expanded,
  busy,

  onToggleExpanded,
  onCreateStep,
  onEditSection,
  onDeleteSection,
  onToggleSectionActive,
  onMoveSectionUp,
  onMoveSectionDown,

  onEditStep,
  onDeleteStep,
  onToggleStepActive,
  onMoveStep,

  onCreateMedia,
  onEditMedia,
  onDeleteMedia,
  onToggleMediaActive,
}) {
  const steps = sortByOrder(
    section.steps || []
  );

  return (
    <section
      className={`overflow-hidden rounded-[26px] border shadow-sm ${
        section.is_active
          ? "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
          : "border-slate-200 bg-slate-100/80 opacity-75 dark:border-slate-800 dark:bg-slate-900/60"
      }`}
    >
      <header className="p-5 sm:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
          <button
            type="button"
            onClick={onToggleExpanded}
            className="flex min-w-0 flex-1 items-start gap-4 text-left"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 font-bold text-white shadow-lg shadow-indigo-500/20">
              {index + 1}
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                  {section.titre}
                </h3>

                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                    section.is_active
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                      : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                  }`}
                >
                  {section.is_active
                    ? "Active"
                    : "Inactive"}
                </span>
              </div>

              <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {section.label} ·{" "}
                {steps.length} étape(s) ·
                ordre {section.ordre}
              </div>

              <div className="mt-1 text-xs text-slate-400">
                #{section.slug}
              </div>
            </div>
          </button>

          <div className="flex flex-wrap items-center gap-2">
            <IconButton
              disabled={
                busy || index === 0
              }
              onClick={
                onMoveSectionUp
              }
              title="Monter"
            >
              <FaArrowUp />
            </IconButton>

            <IconButton
              disabled={
                busy ||
                index === total - 1
              }
              onClick={
                onMoveSectionDown
              }
              title="Descendre"
            >
              <FaArrowDown />
            </IconButton>

            <IconButton
              disabled={busy}
              onClick={
                onToggleSectionActive
              }
              title={
                section.is_active
                  ? "Désactiver"
                  : "Activer"
              }
            >
              {section.is_active ? (
                <FaEyeSlash />
              ) : (
                <FaEye />
              )}
            </IconButton>

            <button
              type="button"
              disabled={busy}
              onClick={onEditSection}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-violet-200 bg-violet-50 text-violet-700 transition hover:bg-violet-100 disabled:opacity-50 dark:border-violet-900/50 dark:bg-violet-500/10 dark:text-violet-300"
              title="Modifier"
            >
              <FaEdit />
            </button>

            <button
              type="button"
              disabled={busy}
              onClick={onDeleteSection}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-700 transition hover:bg-rose-100 disabled:opacity-50 dark:border-rose-900/50 dark:bg-rose-500/10 dark:text-rose-300"
              title="Supprimer"
            >
              <FaTrashAlt />
            </button>

            <button
              type="button"
              onClick={onToggleExpanded}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white dark:bg-white dark:text-slate-900"
              title={
                expanded
                  ? "Réduire"
                  : "Développer"
              }
            >
              {expanded ? (
                <FaChevronUp />
              ) : (
                <FaChevronDown />
              )}
            </button>
          </div>
        </div>
      </header>

      {expanded ? (
        <div className="border-t border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-950/40 sm:p-6">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-slate-50">
                Étapes de la section
              </h4>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Organise le parcours de
                lecture de cette section.
              </p>
            </div>

            <button
              type="button"
              disabled={busy}
              onClick={onCreateStep}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:opacity-50"
            >
              <FaPlus />
              Ajouter une étape
            </button>
          </div>

          {steps.length ? (
            <div className="space-y-4">
              {steps.map(
                (step, stepIndex) => (
                  <TutorialStepCard
                    key={step.id}
                    step={step}
                    index={stepIndex}
                    total={
                      steps.length
                    }
                    busy={busy}
                    onEdit={() =>
                      onEditStep(
                        step,
                        section.id
                      )
                    }
                    onDelete={() =>
                      onDeleteStep(step)
                    }
                    onToggleActive={() =>
                      onToggleStepActive(
                        step
                      )
                    }
                    onMoveUp={() =>
                      onMoveStep(
                        section.id,
                        step.id,
                        "up"
                      )
                    }
                    onMoveDown={() =>
                      onMoveStep(
                        section.id,
                        step.id,
                        "down"
                      )
                    }
                    onCreateMedia={() =>
                      onCreateMedia(step)
                    }
                    onEditMedia={(
                      media
                    ) =>
                      onEditMedia(
                        media,
                        step
                      )
                    }
                    onDeleteMedia={
                      onDeleteMedia
                    }
                    onToggleMediaActive={
                      onToggleMediaActive
                    }
                  />
                )
              )}
            </div>
          ) : (
            <div className="rounded-[24px] border border-dashed border-slate-300 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-900">
              <div className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                Aucune étape
              </div>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Ajoute la première étape
                de cette section.
              </p>

              <button
                type="button"
                onClick={onCreateStep}
                className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white"
              >
                <FaPlus />
                Ajouter une étape
              </button>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}

function IconButton({
  disabled,
  onClick,
  title,
  children,
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-100 disabled:opacity-30 dark:border-slate-700 dark:hover:bg-slate-800"
      title={title}
    >
      {children}
    </button>
  );
}